// What has changed in ops, for people who were here before it changed.
//
// A guided tour teaches somebody a screen. This answers a different question,
// and a more urgent one: "where has the thing I use gone?" Somebody who opens
// ops to approve a holiday and finds the page missing needs telling wherever
// they happen to land, not once they have found the right screen, which is
// exactly what they cannot do. So this is one app-wide notice rather than a
// tour bolted onto each page that moved.
//
// ADD AN ENTRY WHENEVER SOMETHING MOVES, IS RENAMED OR IS TAKEN AWAY.
// Newest last. Write it for somebody who knew the old arrangement and is
// looking for it: say what it was called, where it is now, and what has not
// changed. Resist explaining the feature — its own tour does that.
//
// `page` is the hash route the thing now lives at, so the notice can offer to
// take them there rather than describing the route and leaving them to find it.
export const CHANGE_LOG = [
  {
    id: 'approvals-merged',
    date: '2026-09-21',
    title: 'Approvals: one page instead of two',
    page: 'approvals',
    body: [
      'Approval Requests and Holiday & Absence Requests were separate screens under People. They are now tabs of one page, Approvals, under My Space → Organisation, along with anything hiring that is waiting on you.',
      'Everything you did on either of them you still do there, and who may approve what has not changed. Old links and bookmarks still work. There is one new thing: you can record an absence for somebody in your team rather than waiting for them to file it.',
    ],
  },
  {
    id: 'plow-day-border',
    date: '2026-09-21',
    title: 'PLOW: the traffic light is now the border',
    page: 'plow',
    body: [
      'The red, amber or green dot beside a job on the PLOW grid has gone. The colour is now the border around the job. When AM and PM are on the same job the day shows it once, inside one border coloured by the most urgent of the two; a day split across different jobs has a box per part, each in its own colour.',
      'Setting the traffic light has not changed: it is still the three dots in the cell editor.',
    ],
  },
  {
    id: 'plow-label-row-tasks',
    date: '2026-09-22',
    title: 'PLOW: a label row takes any number of tasks a day',
    page: 'plow',
    body: [
      'A label row on a PLOW (a SERVICING lane, say) used to follow the board\'s sections, so it could hold one job for AM and one for PM and no more. It is now a list: click the day and add as many tasks as there are, in order, each a job or a note with its own traffic light.',
      'Anything already on a label row has been carried across as the first tasks of each day, AM before PM. People\'s rows have not changed.',
    ],
  },
  {
    id: 'rate-bands-page',
    date: '2026-09-22',
    title: 'Rate Bands: one page for everyone\'s band',
    page: 'rate-bands',
    body: [
      'A person\'s rate band used to be visible only by opening their record on the People page, one at a time, and there was nowhere to see who had no band at all. Rate Bands, under People, lists everyone with their band and the day rate the active rate card gives it, flags anyone with no band, and shows the history of every change with who made it.',
      'A band can be assigned or changed there as well as on the People form; either route is recorded in the same history. Who may see the page is set in Roles & Permissions, as for any other page.',
    ],
  },
  {
    id: 'change-approved-leave',
    date: '2026-09-28',
    title: 'My Leave: change approved leave without cancelling it',
    page: 'my-leave',
    body: [
      'To move approved leave you used to cancel it and request the new dates, and the approved week was gone while the new request waited. Approved leave that has not started now has a Change button on My Leave & Holidays.',
      'The change goes to your manager, and your leave stays booked as it was until they decide. If they say no, nothing is lost. Cancelling works as before.',
    ],
  },
  {
    id: 'bid-tracker-crm-redesign',
    date: '2026-09-29',
    title: 'Bid Tracker: new tabs, and the board is the front page',
    page: 'bid-tracker',
    body: [
      'The Bid Tracker opens on Pipeline, the board by stage with five tiles above it. All bids is now Bids, a list of rows rather than a table, and ⚙ Columns is now Fields, which chooses what shows under each row. Report is now Reports, and Review is new: the week, and your bid actions, in one place.',
      'On a bid, Checked portal today and Record outcome are at the top, Merge and Promote to Pipeline are under More, and the timeline, actions, documents and compliance matrix are tabs. Portals are now called Platforms. Nothing you recorded has moved or changed.',
    ],
  },
  {
    id: 'sow-awaiting-payment',
    date: '2026-09-30',
    title: 'SOWs: a new Awaiting Payment status',
    page: 'tracker',
    body: [
      'A SOW whose milestones are all invoiced or paid used to stay Active until the last payment came in. It now moves to Awaiting Payment on its own, and on to Complete when it is paid. It returns to Active by itself if a milestone is added or goes back to Awaiting or Ready to bill.',
      'Because the work is done, these SOWs no longer appear on the PMO and delivery screens or in RAG update chasing. They are still on the Milestones page, you can still log time against them, and the Tracker has an Awaiting Payment tab and card showing what is still owed.',
    ],
  },
  {
    id: 'ecr-tracker-lifecycle',
    date: '2026-10-02',
    title: 'ECR Tracker: new lanes, no review approvals, and a Rolls-Royce SLA',
    page: 'ecr-tracker',
    body: [
      'The board has twelve lanes now. ECR1/2 Preparation is split into ECR1/2 Awaiting Assignment, where every new ECR starts, and ECR1/2 In Progress. Internal Review is ECR1/2 Awaiting AV Review, Awaiting ECR3 is part of ECR1/2 Submitted - Awaiting ECR3, Work In Progress is part of ECR3 Received - Work Active, and Complete has gone: ECR5 Received is the end. ECR4 has the same In Progress, Awaiting AV Review and Ready to Submit steps. Every card was moved to its nearest new lane and keeps its history.',
      'The Internal Review approvals have gone: there are no PM or Infrastructure Review boxes to tick and nothing locks Ready to Submit, so move a card on once its review is done. Target Date is now called ECR1/2 Submission Due Date, with the same dates, and only flags a card as overdue until its ECR1/2 is submitted.',
      'New: Rolls-Royce has 10 working days to respond to each submission, and the card counts them down. Record RR Response on the card logs a query or an amendment request, which stops the clock as well.',
    ],
  },
  {
    id: 'pipeline-radar-retired',
    date: '2026-10-16',
    title: 'Pipeline and Opportunity Radar have gone',
    page: 'bid-tracker',
    body: [
      'The Pipeline page under Commercial has been taken away. Its opportunities went to CRM Leads, which has since gone too: leads are now raised on the Pipeline Tracker. Converting an opportunity to a Draft SOW, deleting one, and Promote to Pipeline on a bid went with it. Old Pipeline links now open the Pipeline Tracker.',
      'Opportunity Radar and its BD Sources settings have gone as well, so ops no longer pulls tender notices from Find a Tender. Bids are tracked by hand in the Pipeline Tracker, as before, and nothing recorded there has changed.',
    ],
  },
  {
    id: 'bid-tracker-renamed',
    date: '2026-10-16',
    title: 'Bid Tracker is now called Pipeline Tracker',
    page: 'bid-tracker',
    body: [
      'The Bid Tracker has a new name, Pipeline Tracker. It is in the same place under Commercial and works the same way: the tabs, your bids, their references and their history are unchanged.',
      'Its reminder emails now start "Pipeline Tracker:" rather than "Bid Tracker:", so update any mail rule that files them by subject.',
    ],
  },
  {
    id: 'pipeline-tracker-leads',
    date: '2026-10-29',
    title: 'Leads are on the Pipeline Tracker',
    page: 'bid-tracker',
    body: [
      'Conversations that are not formal tenders, such as a referral, an enquiry or an MSP renewal, now go on the Pipeline Tracker as leads. Raise one with + New lead and find them on the new Leads tab. A lead has an owner, a next action with a due date, a timeline and people like a bid, but no deadline or portal.',
      'Leads stay out of the Pipeline figures, Triage, Bids and Reports, so the tender numbers are unchanged. When a formal ITT arrives, use Convert to tender on the lead and its history comes with it.',
      'CRM Leads has gone. It held three old opportunities, all closed, which were not carried across. Old CRM Leads links open the Pipeline Tracker.',
    ],
  },
  {
    id: 'documents-open-in-ops',
    date: '2026-11-04',
    title: 'Documents open in Ops',
    body: [
      'Documents on an opportunity, a bid, the Content Library and a person\'s HR record used to open in a new tab ("Open ↗" or "Open in M365 →"). The link now says View and shows the document in Ops: PDFs page by page, Word documents as text, and images. This works for files kept in SharePoint as well as files uploaded to Ops.',
      'Download is in the viewer, and so is Open in SharePoint for a SharePoint file. Ctrl-click (Cmd-click on a Mac) still opens the original in a new tab. Who can see each document has not changed.',
    ],
  },
];

// Seen-ness is recorded in feature_tour_progress, the same per-person table the
// tours use, under a namespaced id. Without the prefix an entry called
// `approvals` would mark the Approvals TOUR as seen and silence it for every
// new starter who never got the notice in the first place.
export const changeTourId = (id) => `change:${id}`;

/**
 * The changes this person should be told about: the ones that happened after
 * they joined and that they have not already been shown.
 *
 * Somebody who arrived after a change never saw the old arrangement, so being
 * told that something "used to be" elsewhere describes a world they never knew,
 * and they are the person least able to tell that it is untrue. Where there is
 * no usable start date this says nothing at all: of the two ways to be wrong,
 * silence is the harmless one.
 */
export function unseenChanges(user, seenTourIds = []) {
  const joined = user?.date_of_joining ? new Date(user.date_of_joining) : null;
  if (!joined || isNaN(joined)) return [];
  const seen = new Set(seenTourIds);
  return CHANGE_LOG.filter((c) => {
    const changed = new Date(c.date);
    if (isNaN(changed) || changed <= joined) return false;
    return !seen.has(changeTourId(c.id));
  });
}
